import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import PaymentLogos from '../payment/PaymentLogos';
import BrandWordmark from './BrandWordmark';
import { useToast } from '../../hooks/use-toast';
import { subscribeNewsletter } from '../../services/newsletter';
import { getApiErrorMessage } from '../../lib/utils';

const Footer = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;

    setSubmitting(true);
    try {
      await subscribeNewsletter(email.trim());
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
    } catch (error) {
      console.error('Newsletter subscribe error:', error);
      toast({
        title: 'Abonelik başarısız',
        description: getApiErrorMessage(
          error,
          'Abonelik kaydedilemedi. Lütfen bir süre sonra tekrar deneyin.'
        ),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const footerLinks = {
    company: [
      { name: 'Yılbaşı Hediye', href: '/pages/yilbasi-hediye', key: 'yilbasi-hediye' },
      { name: 'Sevgililer Günü', href: '/pages/sevgililer-gunu', key: 'sevgililer-gunu' },
      { name: 'Özel Kampanyalar', href: '/pages/ozel-kampanyalar', key: 'ozel-kampanyalar' },
    ],
    help: [
      { name: 'SSS', href: '/pages/sss', key: 'sss' },
      { name: 'İade ve Değişim', href: '/pages/iade-degisim', key: 'iade-degisim' },
      { name: 'Gönderi Ücretleri', href: '/pages/gonderi-ucretleri', key: 'gonderi-ucretleri' },
      { name: 'Müşteri Hizmetleri', href: '/pages/musteri-hizmetleri', key: 'musteri-hizmetleri' },
    ],
    shop: [
      { name: 'Beden Tablosu', href: '/pages/beden-tablosu', key: 'beden-tablosu' },
      { name: 'Sipariş Takip', href: '/order-tracking', key: 'order-tracking' },
      { name: t('common.sale'), href: '/sale', key: 'sale' },
    ],
    legal: [
      { name: 'Hakkımızda', href: '/pages/hakkimizda', key: 'hakkimizda' },
      {
        name: 'Teslimat ve İade Şartları',
        href: '/pages/teslimat-ve-iade-sartlari',
        key: 'teslimat-ve-iade-sartlari',
      },
      {
        name: 'Gizlilik Sözleşmesi',
        href: '/pages/gizlilik-sozlesmesi',
        key: 'gizlilik-sozlesmesi',
      },
      {
        name: 'Mesafeli Satış Sözleşmesi',
        href: '/pages/mesafeli-satis-sozlesmesi',
        key: 'mesafeli-satis-sozlesmesi',
      },
    ],
  };

  return (
    <footer className="bg-gray-100 mt-auto">
      {/* Newsletter Section */}
      <div className="bg-[#1a1a2e] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="text-2xl font-semibold mb-2">{t('footer.newsletter.title')}</h3>
            <p className="text-gray-300 mb-6">
              {t('footer.newsletter.description')}
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-2 sm:flex-row">
              <Input
                type="email"
                placeholder={t('footer.newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                required
              />
              <Button
                type="submit"
                className="bg-white text-[#1a1a2e] hover:bg-gray-200"
                disabled={submitting}
              >
                {submitting ? 'Kaydediliyor...' : t('footer.newsletter.subscribe')}
              </Button>
            </form>
            {subscribed && (
              <p className="mt-3 text-green-400 text-sm">
                {t('footer.newsletter.thankYou')}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-3">
              {t('footer.newsletter.agree')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Company */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.company')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.customerService')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.help.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.shop')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Payment */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.connectWithUs')}
            </h4>
            <div className="flex space-x-4 mb-6">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-black transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-black transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-black transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={24} />
              </a>
            </div>

            <h4 className="font-semibold text-sm uppercase tracking-wider mb-3">
              {t('footer.securePayments')}
            </h4>
            <div className="flex items-center space-x-2 text-gray-500">
              <Lock size={20} />
              <span className="text-xs font-medium">Visa, MasterCard ve iyzico ile güvenli ödeme</span>
            </div>
            <PaymentLogos variant="footer" />
            <p className="text-xs text-gray-500 mt-2">256-bit SSL ile korunan güvenli alışveriş</p>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-500">
          <p className="flex flex-wrap items-center justify-center gap-x-1 sm:justify-start">
            <span>&copy; {new Date().getFullYear()}</span>
            <BrandWordmark size="footer" />
            <span>. {t('footer.allRightsReserved')}</span>
          </p>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            <span>{t('footer.country')}</span>
            <span>|</span>
            <span>{t('footer.currency')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
